import React, { useEffect, useState, useMemo } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from '../../contexts/tour-context';
import { getTourSteps } from './tour-steps';
import TourTooltip from './tour-tooltip';
import s from './styles.module.scss';

const OnboardingTour = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isTourActive,
    currentStepIndex,
    setCurrentStepIndex,
    skipTour,
    markTourCompleted,
    isMobile
  } = useTour();

  // Generate steps based on mobile state
  const tourSteps = useMemo(() => getTourSteps(isMobile), [isMobile]);

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Wait for element to exist in DOM using MutationObserver
  const waitForElement = (selector, timeout = 5000) => {
    return new Promise((resolve, reject) => {
      // Check immediately
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
        return;
      }

      // Set up observer
      const observer = new MutationObserver((mutations) => {
        if (document.querySelector(selector)) {
          observer.disconnect();
          resolve(document.querySelector(selector));
        }
      });

      // Start observing
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Timeout fallback
      setTimeout(() => {
        observer.disconnect();
        reject(new Error(`Element ${selector} not found within ${timeout}ms`));
      }, timeout);
    });
  };

  // Sync with context
  useEffect(() => {
    setRun(isTourActive);
    setStepIndex(currentStepIndex);
  }, [isTourActive, currentStepIndex]);

  // Handle Joyride callback with async element polling
  const handleJoyrideCallback = async (data) => {
    const { action, index, status, type } = data;

    // Tour finished
    if (status === STATUS.FINISHED) {
      setRun(false);
      markTourCompleted();
      return;
    }

    // Tour skipped
    if (status === STATUS.SKIPPED) {
      setRun(false);
      skipTour();
      return;
    }

    // Handle close button
    if (action === ACTIONS.CLOSE) {
      setRun(false);
      skipTour();
      return;
    }

    // Step change
    if (type === EVENTS.STEP_AFTER) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      // Check bounds
      if (nextStepIndex < 0) {
        setStepIndex(0);
        setCurrentStepIndex(0);
        return;
      }

      if (nextStepIndex >= tourSteps.length) {
        setRun(false);
        markTourCompleted();
        return;
      }

      const nextStep = tourSteps[nextStepIndex];
      const currentPath = location.pathname;

      // Pause tour during transition
      setRun(false);
      setIsTransitioning(true);

      try {
        // Navigate if needed
        if (nextStep.route && nextStep.route !== currentPath) {
          console.log(`🧭 Navigating to ${nextStep.route}`);
          navigate(nextStep.route);
        }

        // Wait for target element to exist
        console.log(`⏳ Waiting for element: ${nextStep.target}`);
        await waitForElement(nextStep.target, 5000);
        console.log(`✅ Element found: ${nextStep.target}`);

        // Extra delay to ensure full rendering
        await new Promise(resolve => setTimeout(resolve, 300));

        // Update step index and resume tour
        setStepIndex(nextStepIndex);
        setCurrentStepIndex(nextStepIndex);
        setIsTransitioning(false);
        setRun(true);

      } catch (error) {
        console.error(`❌ Step ${nextStepIndex + 1} target not found:`, error);
        setIsTransitioning(false);

        // Ask user if they want to skip or end tour
        const shouldSkip = window.confirm(
          `The next tour step can't be displayed because the target element isn't visible. Would you like to skip to the following step?\n\nClick OK to skip, or Cancel to end the tour.`
        );

        if (shouldSkip && nextStepIndex + 1 < tourSteps.length) {
          // Recursively try next step
          await handleJoyrideCallback({
            action: ACTIONS.NEXT,
            index: nextStepIndex,
            status: STATUS.RUNNING,
            type: EVENTS.STEP_AFTER
          });
        } else {
          // End tour
          setRun(false);
          skipTour();
        }
      }
    }
  };

  // Don't render if tour is not active
  if (!isTourActive) {
    return null;
  }

  return (
    <>
      {/* Tour component */}
      <Joyride
        key={isMobile ? 'mobile' : 'desktop'}
        steps={tourSteps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showProgress={false}
        showSkipButton
        disableOverlayClose
        disableCloseOnEsc={false}
        disableScrolling={!isMobile}
        disableScrollParentFix={isMobile}
        scrollOffset={isMobile ? 50 : 100}
        spotlightPadding={isMobile ? 0 : 2}
        callback={handleJoyrideCallback}
        tooltipComponent={TourTooltip}
        styles={{
          options: {
            zIndex: 10000,
            arrowColor: 'white',
            backgroundColor: 'white',
            primaryColor: '#3b82f6',
            textColor: '#1f2937',
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          },
          spotlight: {
            borderRadius: '8px'
          }
        }}
        floaterProps={{
          disableAnimation: isMobile,
          offset: isMobile ? 8 : 15,
          styles: {
            arrow: {
              length: 8,
              spread: 16
            },
            floater: {
              filter: 'drop-shadow(0 4px 20px rgba(0, 0, 0, 0.15))'
            }
          },
          options: {
            preventOverflow: {
              boundariesElement: 'viewport',
              padding: isMobile ? 8 : 20
            }
          }
        }}
      />
    </>
  );
};

export default OnboardingTour;
